// Forgot Password - self-service reset entry.
// Step 'email': ask for the account email. The backend ALWAYS answers with the
// same neutral message (real account or not), so this page always advances to
// the code step - never branch UI on whether the account exists.
// Step 'code': 6-digit code emailed to real accounts. On a correct code the
// backend returns the standard set-password sessionToken; we store it exactly
// like the login reset flow does and hand off to the existing /auth/np2w8q
// Set Password page (which finishes with auto-login).
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Input, notification } from 'antd';
import { ThemeForm, ThemeButton } from '../Styles/Theme';
import { LoginNewWrapper } from '../Login/LoginNewStyle';
import LoginLeftPanel from '../Login/LoginLeftPanel';
import { requestPasswordReset, verifyPasswordResetCode } from '../../services/forgotPassword.service';
import { STORAGE_KEYS } from '../../utils/constants';

const RESEND_COOLDOWN_SECONDS = 65;
const OTP_LENGTH = 6;

// Segmented 6-box code input, hand-built on purpose: antd's Input.OTP only
// fires onChange when ALL cells are filled, so clearing a cell leaves a stale
// value and the wrong thing gets submitted. Here EVERY keystroke, delete and
// paste updates `value` - so what is on screen is always exactly what submits.
const OtpBoxes = ({ value, onChange, onEnter, disabled }) => {
	const refs = useRef([]);
	const chars = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] || '');

	const focusBox = (i) => {
		const el = refs.current[Math.max(0, Math.min(OTP_LENGTH - 1, i))];
		if (el) el.focus();
	};

	const handleChange = (i, raw) => {
		const digits = raw.replace(/\D/g, '');
		const arr = [...chars];
		if (!digits) {
			// cleared this box
			arr[i] = '';
			onChange(arr.join(''));
			return;
		}
		// take the LAST digit typed (handles typing over a filled box)
		arr[i] = digits[digits.length - 1];
		onChange(arr.join(''));
		if (i < OTP_LENGTH - 1) focusBox(i + 1);
	};

	const handleKeyDown = (i, e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (onEnter) onEnter();
			return;
		}
		if (e.key === 'Backspace') {
			e.preventDefault();
			const arr = [...chars];
			if (chars[i]) {
				arr[i] = '';
				onChange(arr.join(''));
			} else if (i > 0) {
				arr[i - 1] = '';
				onChange(arr.join(''));
				focusBox(i - 1);
			}
			return;
		}
		if (e.key === 'ArrowLeft') focusBox(i - 1);
		if (e.key === 'ArrowRight') focusBox(i + 1);
	};

	const handlePaste = (i, e) => {
		e.preventDefault();
		const digits = (e.clipboardData.getData('text') || '').replace(/\D/g, '');
		if (!digits) return;
		const arr = [...chars];
		let j = i;
		for (const d of digits) {
			if (j >= OTP_LENGTH) break;
			arr[j] = d;
			j += 1;
		}
		onChange(arr.join(''));
		focusBox(j);
	};

	return (
		<div style={{ display: 'inline-flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', maxWidth: '100%' }}>
			{chars.map((c, i) => (
				<input
					key={i}
					ref={(el) => {
						refs.current[i] = el;
					}}
					type='text'
					inputMode='numeric'
					autoComplete={i === 0 ? 'one-time-code' : 'off'}
					value={c}
					disabled={disabled}
					aria-label={`Digit ${i + 1}`}
					onChange={(e) => handleChange(i, e.target.value)}
					onKeyDown={(e) => handleKeyDown(i, e)}
					onPaste={(e) => handlePaste(i, e)}
					onFocus={(e) => e.target.select()}
					style={{
						width: 48,
						height: 56,
						textAlign: 'center',
						fontSize: 22,
						fontWeight: 600,
						fontFamily: 'monospace',
						border: '1px solid #d9d9d9',
						borderRadius: 10,
						color: '#0A1B39',
						background: disabled ? '#f5f5f5' : '#fff',
					}}
				/>
			))}
		</div>
	);
};

const ForgotPassword = () => {
	const navigate = useNavigate();
	const [form] = ThemeForm.useForm(); // email step only
	const [step, setStep] = useState('email'); // 'email' | 'code'
	const [email, setEmail] = useState('');
	const [loading, setLoading] = useState(false);
	const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
	const [code, setCode] = useState('');
	const [codeError, setCodeError] = useState('');
	const timerRef = useRef(null);

	useEffect(() => {
		if (resendSecondsLeft <= 0) return undefined;
		timerRef.current = setTimeout(() => setResendSecondsLeft((s) => s - 1), 1000);
		return () => clearTimeout(timerRef.current);
	}, [resendSecondsLeft]);

	const handleEmailSubmit = async (values) => {
		const cleanEmail = String(values.email || '').trim().toLowerCase();
		setLoading(true);
		const result = await requestPasswordReset(cleanEmail);
		setLoading(false);
		if (result.success) {
			setEmail(cleanEmail);
			setCode('');
			setCodeError('');
			setStep('code');
			setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
			notification.success({ message: 'Check your inbox', description: result.message });
		} else {
			notification.error({
				message: 'Error',
				description: result.message || 'Something went wrong. Please try again.',
			});
		}
	};

	const handleVerifyCode = async () => {
		// Guard client-side so an incomplete code never burns a backend attempt.
		if (!/^\d{6}$/.test(code)) {
			setCodeError('Please enter the full 6-digit code');
			return;
		}
		setCodeError('');
		setLoading(true);
		const result = await verifyPasswordResetCode(email, code);
		setLoading(false);
		if (result.success && result.data?.sessionToken) {
			localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, result.data.sessionToken);
			if (result.data.user) {
				localStorage.setItem(STORAGE_KEYS.PENDING_USER, JSON.stringify(result.data.user));
			}
			navigate('/auth/np2w8q');
		} else {
			notification.error({
				message: 'Verification failed',
				description: result.message || 'The code is incorrect or has expired.',
			});
		}
	};

	const handleResend = async () => {
		if (resendSecondsLeft > 0 || loading) return;
		setLoading(true);
		const result = await requestPasswordReset(email);
		setLoading(false);
		if (result.success) {
			setCode('');
			setCodeError('');
			setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
			notification.success({ message: 'Check your inbox', description: result.message });
		} else {
			notification.error({
				message: 'Error',
				description: result.message || 'Could not resend the code. Please try again.',
			});
		}
	};

	const handleBackToEmail = () => {
		setStep('email');
		setResendSecondsLeft(0);
		setCode('');
		setCodeError('');
		form.resetFields();
	};

	const footer = (
		<div className='loginFooter'>
			<span>@{new Date().getFullYear()} CrowdVox Inc.</span>
		</div>
	);

	return (
		<LoginNewWrapper>
			<Row gutter={[20, 16]}>
				<Col
					xs={{ span: 24, order: 1 }}
					sm={{ span: 24, order: 1 }}
					md={{ span: 12, order: 2 }}
					lg={{ span: 14, order: 2 }}
					xl={{ span: 14, order: 2 }}
				>
					<div className='loginRightCard'>
						<div className='loginLogo'>
							<img src='/images/login-new/crowdvox-logo.svg' alt='CrowdVox' />
						</div>

						<div className='loginContainer'>
							{step === 'email' ? (
								<>
									<h1>Forgot Password</h1>
									<p>Enter your email address and we will send you a verification code.</p>
									<ThemeForm
										form={form}
										name='forgotPasswordEmailForm'
										layout='vertical'
										autoComplete='off'
										onFinish={handleEmailSubmit}
										className='loginFormV2'
									>
										<ThemeForm.Item
											name='email'
											label='Email'
											rules={[
												{ required: true, message: 'Please enter your email' },
												{ type: 'email', message: 'Please enter a valid email' },
											]}
										>
											<Input
												prefix={<img src='/images/login-new/icon-envelope.svg' alt='' />}
												size='large'
												placeholder='Enter your email'
												autoComplete='email'
											/>
										</ThemeForm.Item>
										<ThemeButton type='primary' htmlType='submit' size='large' block loading={loading}>
											Send Code
										</ThemeButton>
									</ThemeForm>
									<p style={{ marginTop: 16 }}>
										<a onClick={() => navigate('/login')}>Back to sign in</a>
									</p>
								</>
							) : (
								<>
									<h1>Enter Verification Code</h1>
									<p>
										If an account exists for {email}, a{' '}
										<span style={{ whiteSpace: 'nowrap' }}>6-digit</span> code is on its way. Enter it
										below.
									</p>

									<div style={{ textAlign: 'center', marginTop: 8 }}>
										<OtpBoxes
											value={code}
											onChange={(v) => {
												setCode(v);
												if (codeError) setCodeError('');
											}}
											onEnter={handleVerifyCode}
											disabled={loading}
										/>
										{codeError && (
											<p style={{ color: '#ff4d4f', fontSize: 13, margin: '10px 0 0' }}>{codeError}</p>
										)}
									</div>

									<ThemeButton
										type='primary'
										size='large'
										block
										loading={loading}
										onClick={handleVerifyCode}
										style={{ marginTop: 20 }}
									>
										Verify Code
									</ThemeButton>

									<p style={{ marginTop: 16 }}>
										{resendSecondsLeft > 0 ? (
											<span>{`Resend code in ${resendSecondsLeft}s`}</span>
										) : (
											<a onClick={handleResend}>Resend code</a>
										)}
										<span style={{ margin: '0 8px' }}>|</span>
										<a onClick={handleBackToEmail}>Use a different email</a>
									</p>
								</>
							)}
						</div>
						{footer}
					</div>
				</Col>
				<Col
					xs={{ span: 0, order: 2 }}
					sm={{ span: 0, order: 2 }}
					md={{ span: 12, order: 1 }}
					lg={{ span: 10, order: 1 }}
					xl={{ span: 10, order: 1 }}
				>
					<LoginLeftPanel />
				</Col>
			</Row>
		</LoginNewWrapper>
	);
};

export default ForgotPassword;
